


















































































































































import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch
} from '@vue/composition-api';
import {
  useCreateInvoiceItem,
  useUpdateInvoiceItem
} from '@/composable/invoice';
import { AxiosError } from 'axios';

import { cloneDeep } from 'lodash';
import { diffDeep, setErrorFromAxiosError } from '@/utils/helpers';
import {
  IInvoiceItem,
  IInvoiceItemData,
  InvoiceItem,
  ITaxData,
  PaymentPlanAmountTypeEnum
} from '@/models';
import { ISystemError } from '@/models/error/error.types';
import { ALForm } from '@/common/component.types';
import { requiredValidator } from '@/utils/validators';

import ErrorAlert from '@/components/common/ErrorAlert.vue';
import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import TaxAutoCompletePicker from '@/components/tax/TaxAutoCompletePicker.vue';

export default defineComponent({
  name: 'InvoiceItemDialog',
  components: { ALDialog, ErrorAlert, TaxAutoCompletePicker },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    invoiceId: {
      type: Number,
      required: true
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    invoiceAmountType: {
      type: Number,
      default: PaymentPlanAmountTypeEnum.FixedAmount
    },
    invoiceItem: {
      type: Object as PropType<IInvoiceItem | null>,
      default: null
    }
  },
  emits: ['update:dialog'],
  setup(props, { emit }) {
    const localDialog = computed({
      get: () => props.dialog,
      set: value => {
        emit('update:dialog', value);
      }
    });

    const invoiceItemData = ref<IInvoiceItemData>({});

    watch(localDialog, value => {
      if (value) {
        clearErrors();
        invoiceItemData.value = !props.isEdit
          ? ({} as IInvoiceItemData)
          : cloneDeep(props.invoiceItem!);

        // Set tax name and rate if tax is provided
        updateTaxInfo(invoiceItemData.value.tax ?? null);
      }
    });

    const formRef = ref<ALForm | null>(null);
    const valid = ref(false);

    const subtitle = computed(
      () => `${props.isEdit ? 'Update' : 'Create'} payment plan information`
    );

    function closeDialog() {
      clearErrors();
      localDialog.value = false;
    }

    function updateTaxInfo(tax: ITaxData | null) {
      clearErrors();
      if (tax != null) {
        invoiceItemData.value.tax_name = tax.name;
        invoiceItemData.value.tax_rate = tax.rate;
      }
    }

    function clearTaxInfo() {
      if (!invoiceItemData.value.tax_name) {
        invoiceItemData.value.tax_name = '';
        invoiceItemData.value.tax = null;
      }
    }

    const {
      isLoading: isUpdatePending,
      error: updateError,
      mutate: updateInvoiceItem,
      reset: updateReset
    } = useUpdateInvoiceItem(props.invoiceId);
    const {
      isLoading: isCreatePending,
      error: createError,
      mutate: createInvoiceItem,
      reset: createReset
    } = useCreateInvoiceItem(props.invoiceId);

    const localError = ref<ISystemError | null>(null);

    const loading = computed(() =>
      props.isEdit ? isUpdatePending.value : isCreatePending.value
    );

    const error = computed<ISystemError | null>({
      get() {
        let systemError: ISystemError | null = localError.value;
        const axiosError = props.isEdit ? updateError.value : createError.value;

        if (axiosError) {
          systemError = setErrorFromAxiosError(axiosError as AxiosError);
        }

        return systemError;
      },
      set(value: ISystemError | null) {
        localError.value = value;
      }
    });

    function clearErrors() {
      props.isEdit ? updateReset() : createReset();
      error.value = null;
    }

    async function save() {
      clearErrors();
      const valid = await formRef.value!.validate();

      if (valid) {
        let invoiceItemInstance: IInvoiceItem | null = null;

        try {
          invoiceItemInstance = new InvoiceItem(invoiceItemData.value);
        } catch (e) {
          // When constructing the instance before saving the possibility of an error can occur the local error is to store that error instance and display it
          error.value = e as ISystemError;
        }

        if (invoiceItemInstance != null) {
          const saveAction = props.isEdit
            ? updateInvoiceItem
            : createInvoiceItem;

          let payload = invoiceItemInstance;
          if (props.isEdit) {
            payload = diffDeep(invoiceItemInstance, props.invoiceItem, true);
          }

          // because the payload will not contain a false value for tax_name and tax_rate,
          // we need to add it to the payload manually
          if (!invoiceItemData.value.tax_name) {
            payload.tax_name = '';
          }
          if (!invoiceItemData.value.tax_rate) {
            payload.tax_rate = null;
          }

          saveAction(payload, {
            onSuccess: () => {
              closeDialog();
            }
          });
        }
      }
    }

    return {
      requiredValidator,
      PaymentPlanAmountTypeEnum,
      localDialog,
      invoiceItemData,
      valid,
      formRef,
      subtitle,
      closeDialog,
      save,
      clearErrors,
      updateTaxInfo,
      clearTaxInfo,
      loading,
      error
    };
  }
});
